import React, { useEffect, useRef, useState } from 'react';
import { FiStar, FiCamera, FiX, FiCheck, FiUser } from 'react-icons/fi';
import { productAPI } from '../api/services';
import { useAuthStore } from '../store/authStore';
import './ReviewSection.css';

function StarPicker({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="rs-star-picker">
      {[1, 2, 3, 4, 5].map(s => (
        <button
          key={s}
          type="button"
          className={`rs-star-pick ${s <= (hovered || value) ? 'active' : ''}`}
          onMouseEnter={() => setHovered(s)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(s)}
        >
          <FiStar size={22} />
        </button>
      ))}
      <span className="rs-star-label">
        {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][hovered || value]}
      </span>
    </div>
  );
}

function ReviewSection({ productId, productName }) {
  const [reviews, setReviews]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ rating: 5, comment: '', reviewer_name: '', image: null });
  const [preview, setPreview]   = useState(null);
  const fileRef = useRef();
  const { user, token } = useAuthStore();

  useEffect(() => { fetchReviews(); }, [productId]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const r = await productAPI.getProductReviews(productId);
      setReviews(r.data);
    } catch {}
    finally { setLoading(false); }
  };

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm(f => ({ ...f, image: file }));
    setPreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setForm(f => ({ ...f, image: null }));
    setPreview(null);
    fileRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.comment.trim()) return;
    if (!token && !form.reviewer_name.trim()) return;
    setSubmitting(true);
    try {
      await productAPI.createReview(productId, {
        rating: form.rating,
        comment: form.comment,
        reviewer_name: token ? undefined : form.reviewer_name,
        image: form.image || undefined,
      });
      setSubmitted(true);
      setForm({ rating: 5, comment: '', reviewer_name: '', image: null });
      setPreview(null);
    } catch {
      alert('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const LABEL = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

  const avgRating = reviews.length
    ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length
    : 0;
  const starCounts = [5, 4, 3, 2, 1].map(star => reviews.filter(r => r.rating === star).length);

  return (
    <div className="rs-wrap">
      <div className="rs-header">
        <h2 className="rs-title">Ratings &amp; Reviews{productName ? ` of ${productName}` : ''}</h2>
      </div>

      {/* ── Breakdown + Write-a-review, side by side ── */}
      <div className="rs-top-grid">

        {/* Rating breakdown */}
        <div className="rs-summary">
          <div className="rs-summary-score">
            <span className="rs-summary-avg">{avgRating.toFixed(1)}<span className="rs-summary-max">/5</span></span>
            <div className="rs-stars">
              {[1,2,3,4,5].map(s => (
                <FiStar key={s} size={14} className={s <= Math.round(avgRating) ? 'rs-star-on' : 'rs-star-off'} />
              ))}
            </div>
            <span className="rs-summary-count">{reviews.length} Rating{reviews.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="rs-summary-bars">
            {[5, 4, 3, 2, 1].map((star, i) => {
              const count = starCounts[i];
              const pct = reviews.length ? (count / reviews.length) * 100 : 0;
              return (
                <div key={star} className="rs-bar-row">
                  <span className="rs-bar-star"><FiStar size={12} className="rs-star-on" /> {star}</span>
                  <div className="rs-bar-track"><div className="rs-bar-fill" style={{ width: `${pct}%` }} /></div>
                  <span className="rs-bar-count">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Write a review */}
        <div className="rs-form-card">
          <h3 className="rs-form-title">Write a Review</h3>

          {submitted ? (
            <div className="rs-success">
              <div className="rs-success-icon"><FiCheck size={22} /></div>
              <div>
                <p className="rs-success-title">Your review has been recorded.</p>
                <p className="rs-success-sub">Thank you for your feedback!</p>
              </div>
              <button className="rs-success-again" onClick={() => setSubmitted(false)}>Write another</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Name (guests only) */}
              {!token && (
                <div className="rs-field">
                  <label className="rs-label">Your Name *</label>
                  <div className="rs-input-wrap">
                    <FiUser size={15} className="rs-input-icon" />
                    <input
                      className="rs-input"
                      type="text"
                      placeholder="Enter your name"
                      value={form.reviewer_name}
                      onChange={e => setForm(f => ({ ...f, reviewer_name: e.target.value }))}
                      required
                    />
                  </div>
                </div>
              )}

              {/* Star rating */}
              <div className="rs-field">
                <label className="rs-label">Rating *</label>
                <StarPicker value={form.rating} onChange={v => setForm(f => ({ ...f, rating: v }))} />
              </div>

              {/* Comment */}
              <div className="rs-field">
                <label className="rs-label">Your Review *</label>
                <textarea
                  className="rs-textarea"
                  placeholder="Share your experience with this product…"
                  rows={4}
                  value={form.comment}
                  onChange={e => setForm(f => ({ ...f, comment: e.target.value }))}
                  required
                />
              </div>

              {/* Photo */}
              <div className="rs-field">
                <label className="rs-label">Photo <span className="rs-optional">(optional)</span></label>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImage} />
                {preview ? (
                  <div className="rs-img-preview-wrap">
                    <img src={preview} alt="preview" className="rs-img-preview" />
                    <button type="button" className="rs-img-remove" onClick={removeImage}><FiX size={14} /></button>
                  </div>
                ) : (
                  <button type="button" className="rs-upload-btn" onClick={() => fileRef.current.click()}>
                    <FiCamera size={16} /> Add Photo
                  </button>
                )}
              </div>

              <button type="submit" className="rs-submit-btn" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit Review'}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* ── Reviews list ── */}
      <div className="rs-list">
        {loading ? (
          [1,2].map(i => <div key={i} className="rs-skeleton" />)
        ) : reviews.length === 0 ? (
          <div className="rs-empty">
            <div className="rs-empty-icon">★</div>
            <p>No reviews yet. Be the first!</p>
          </div>
        ) : reviews.map(rv => (
          <div key={rv.id} className="rs-item">
            <div className="rs-item-top">
              <div className="rs-avatar">{(rv.reviewer_name || 'A')[0].toUpperCase()}</div>
              <div className="rs-item-meta">
                <span className="rs-reviewer">{rv.reviewer_name || 'Customer'}</span>
                <div className="rs-stars">
                  {[1,2,3,4,5].map(s => (
                    <FiStar key={s} size={12} className={s <= rv.rating ? 'rs-star-on' : 'rs-star-off'} />
                  ))}
                  <span className="rs-rating-label">{LABEL[rv.rating]}</span>
                </div>
              </div>
              <span className="rs-date">{new Date(rv.created_at).toLocaleDateString('en-PK', { day:'numeric', month:'short', year:'numeric' })}</span>
            </div>
            <p className="rs-comment">{rv.comment}</p>
            {rv.image_url && (
              <img src={rv.image_url} alt="review" className="rs-review-img" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default ReviewSection;
